import { startOnePatchRum } from "../onepatch_rum"
import "@hotwired/turbo-rails"
import "../controllers"
import React, { Suspense } from "react"
import { createRoot } from "react-dom/client"
import MortgageCalculator from "../components/MortgageCalculator"
import ErrorBoundary from "../components/ErrorBoundary"
import PolandVsEuropeDashboard from "../components/PolandVsEuropeDashboard"

const RentVsBuyCalculator = React.lazy(() => import("../components/RentVsBuyCalculator"))
const PropertyValuationWizard = React.lazy(() => import("../components/PropertyValuationWizard"))
const AffordabilityCalculator = React.lazy(() => import("../components/AffordabilityCalculator"))
const PriceChart = React.lazy(() => import("../components/PriceChart"))
const HousingStockChart = React.lazy(() => import("../components/HousingStockChart"))
const PolandHousingMap = React.lazy(() => import("../components/PolandHousingMap"))
const CityDistrictMap = React.lazy(() => import("../components/CityDistrictMap"))
const WiborChart = React.lazy(() => import("../components/WiborChart"))
const RcnExplorer = React.lazy(() => import("../components/RcnExplorer"))
const ListingsBrowseMap = React.lazy(() => import("../components/ListingsBrowseMap"))
const SoldUnitsMap = React.lazy(() => import("../components/SoldUnitsMap"))
const ProPublicListingMap = React.lazy(() => import("../../../../pro/app/javascript/components/pro/ProPublicListingMap"))
import { initDataTableWraps } from "../data_table_scroll"
import { wrapGuideTables } from "../guide_table_wrap"
import { linkGlossaryTerms } from "../glossary/linker"
import { initPublicSiteI18n } from "../i18n"
import { initListingsBrowsePage } from "../listings_browse"

// Browser telemetry. No-ops unless the server rendered the onepatch-rum meta tag.
startOnePatchRum()

const i18n = initPublicSiteI18n(document.documentElement.lang)

const roots = new Map()

function loadingHtml() {
  return `<div class="calculator-loading"><p class="text-sm font-semibold text-ink">${i18n.t("common.loading.calculator")}</p><p class="text-xs text-muted mt-1">${i18n.t("common.loading.calculatorHint")}</p></div>`
}

function calculatorErrorHtml() {
  return `<div class="rounded-xl border border-negative/30 bg-negative/5 p-6"><p class="font-semibold text-negative">${i18n.t("common.errors.calculatorFailed")}</p><p class="text-sm text-muted mt-2">${i18n.t("common.errors.calculatorFailedHint")}</p></div>`
}

function resetMountPoint(el) {
  if (!el) return
  delete el.dataset.mounted
  el.innerHTML = ""
}

function unmountAll() {
  roots.forEach((root, id) => {
    root.unmount()
    resetMountPoint(document.getElementById(id))
  })
  roots.clear()
  document.querySelectorAll(".guide-embed[data-mounted]").forEach((el) => {
    delete el.dataset.mounted
    el.innerHTML = ""
  })
}

function mount(id, Component, props = {}) {
  const el = document.getElementById(id)
  if (!el) return

  if (roots.has(id)) {
    roots.get(id).unmount()
    roots.delete(id)
  }

  try {
    const root = createRoot(el)
    root.render(
      <ErrorBoundary>
        <Suspense fallback={<div dangerouslySetInnerHTML={{ __html: loadingHtml() }} />}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    )
    roots.set(id, root)
    el.dataset.mounted = "true"
  } catch (error) {
    console.error(`[calculators] failed to mount ${id}`, error)
    el.innerHTML = calculatorErrorHtml()
  }
}

function mountCalculators() {
  const mortgageEl = document.getElementById("mortgage-calculator-root")
  if (mortgageEl) {
    mount("mortgage-calculator-root", MortgageCalculator, {
      defaultRate: parseFloat(mortgageEl.dataset.rate || "6.75"),
      wibor6m: mortgageEl.dataset.wibor ? parseFloat(mortgageEl.dataset.wibor) : null,
      marginPp: mortgageEl.dataset.margin ? parseFloat(mortgageEl.dataset.margin) : null,
      sourcePage: mortgageEl.dataset.sourcePage || window.location.pathname,
    })
  }

  if (document.getElementById("rent-vs-buy-root")) {
    const rvbEl = document.getElementById("rent-vs-buy-root")
    let cities = []
    try {
      cities = JSON.parse(rvbEl.dataset.cities || "[]")
    } catch {
      cities = []
    }
    mount("rent-vs-buy-root", RentVsBuyCalculator, {
      defaultRent: parseFloat(rvbEl.dataset.defaultRent || "4500"),
      defaultPrice: parseFloat(rvbEl.dataset.defaultPrice || "800000"),
      defaultRate: parseFloat(rvbEl.dataset.defaultRate || "6.75"),
      cities,
    })
  }

  const valuationEl = document.getElementById("property-valuation-root")
  if (valuationEl) {
    mount("property-valuation-root", PropertyValuationWizard, {
      proSignupUrl: valuationEl.dataset.proSignupUrl || "/pro/users/sign_up",
      initialToken: valuationEl.dataset.initialToken || null,
      turnstileSiteKey: valuationEl.dataset.turnstileSiteKey || null,
    })
  }

  if (document.getElementById("affordability-root")) {
    const affEl = document.getElementById("affordability-root")
    let cities = []
    try {
      cities = JSON.parse(affEl.dataset.cities || "[]")
    } catch {
      cities = []
    }
    mount("affordability-root", AffordabilityCalculator, {
      defaultRate: parseFloat(affEl.dataset.rate || "6.75"),
      cities,
    })
  }

  const chartEl = document.getElementById("price-chart-root")
  if (chartEl) {
    mount("price-chart-root", PriceChart, {
      citySlug: chartEl.dataset.citySlug,
      cityName: chartEl.dataset.cityName,
    })
  }

  const stockChartEl = document.getElementById("housing-stock-chart-root")
  if (stockChartEl) {
    mount("housing-stock-chart-root", HousingStockChart, {
      segments: stockChartEl.dataset.segments,
      cityName: stockChartEl.dataset.cityName,
    })
  }

  if (document.getElementById("poland-housing-map-root")) {
    mount("poland-housing-map-root", PolandHousingMap)
  }

  const districtMapEl = document.getElementById("city-district-map-root")
  if (districtMapEl) {
    mount("city-district-map-root", CityDistrictMap, {
      citySlug: districtMapEl.dataset.citySlug,
      cityName: districtMapEl.dataset.cityName,
      highlightSlug: districtMapEl.dataset.highlightSlug || null,
      compact: districtMapEl.dataset.compact === "true",
      metric: districtMapEl.dataset.metric || null,
    })
  }

  if (document.getElementById("poland-vs-europe-root")) {
    mount("poland-vs-europe-root", PolandVsEuropeDashboard)
  }

  if (document.getElementById("rcn-explorer-root")) {
    const rcnEl = document.getElementById("rcn-explorer-root")
    mount("rcn-explorer-root", RcnExplorer, {
      initialLat: rcnEl.dataset.lat ? parseFloat(rcnEl.dataset.lat) : null,
      initialLng: rcnEl.dataset.lng ? parseFloat(rcnEl.dataset.lng) : null,
      initialRadius: rcnEl.dataset.radius ? parseInt(rcnEl.dataset.radius, 10) : null,
      initialAddress: rcnEl.dataset.address || null,
      initialQuery: rcnEl.dataset.query || null,
    })
  }

  if (document.getElementById("listings-browse-map-root")) {
    const mapEl = document.getElementById("listings-browse-map-root")
    let mapProps = {}
    try {
      mapProps = JSON.parse(mapEl.dataset.props || "{}")
    } catch {
      mapProps = {}
    }
    mount("listings-browse-map-root", ListingsBrowseMap, mapProps)
  }

  if (document.getElementById("sold-units-map-root")) {
    mount("sold-units-map-root", SoldUnitsMap, {})
  }

  mountGuideEmbeds()
  mountPublicListingMaps()
}

function mountPublicListingMaps() {
  document.querySelectorAll("[data-public-listing-map]:not([data-mounted])").forEach((el, index) => {
    if (!el.id) el.id = `public-listing-map-${index}`

    let props = {}
    try {
      props = JSON.parse(el.dataset.props || "{}")
    } catch {
      return
    }

    if (!props.lat || !props.lng) return

    try {
      const root = createRoot(el)
      root.render(
        <Suspense fallback={null}>
          <ProPublicListingMap
            lat={props.lat}
            lng={props.lng}
            radiusM={props.radius_m ?? props.radiusM ?? 400}
            exact={props.exact ?? false}
          />
        </Suspense>
      )
      roots.set(el.id, root)
      el.dataset.mounted = "true"
    } catch (error) {
      console.error("[public-listing-map] failed to mount", error)
    }
  })
}

function mountGuideEmbeds() {
  document.querySelectorAll(".guide-embed:not([data-mounted])").forEach((el, index) => {
    const embed = el.dataset.embed
    const mountId = `guide-embed-${index}-${embed}`
    el.id = mountId
    el.dataset.mounted = "true"

    const props = {
      citySlug: el.dataset.citySlug,
      cityName: el.dataset.cityName,
      metric: el.dataset.metric || null,
    }

    switch (embed) {
      case "price_chart":
        mount(mountId, PriceChart, props)
        break
      case "city_districts":
        mount(mountId, CityDistrictMap, { ...props, compact: el.dataset.compact === "true" })
        break
      case "housing_map":
        mount(mountId, PolandHousingMap)
        break
      case "wibor_chart":
        mount(mountId, WiborChart)
        break
      case "europe_compare":
        mount(mountId, PolandVsEuropeDashboard)
        break
      default:
        break
    }
  })
}

function setupNewsletter() {
  const form = document.getElementById("newsletter-form")
  if (!form || form.dataset.bound === "true") return

  form.dataset.bound = "true"
  form.addEventListener("submit", async (e) => {
    e.preventDefault()
    const msg = document.getElementById("newsletter-message")
    const formData = new FormData(form)
    try {
      const res = await fetch("/api/v1/subscribers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": formData.get("authenticity_token"),
        },
        body: JSON.stringify({ subscriber: { email: formData.get("subscriber[email]") } }),
      })
      if (res.ok) {
        msg.textContent = i18n.t("common.newsletter.subscribed")
        msg.classList.remove("hidden", "text-negative")
        msg.classList.add("text-positive")
        form.reset()
      } else {
        throw new Error("Failed")
      }
    } catch {
      msg.textContent = i18n.t("common.newsletter.failed")
      msg.classList.remove("hidden")
      msg.classList.add("text-negative")
    }
  })
}

function setupCityListRows() {
  document.querySelectorAll(".city-list-row[data-href]").forEach((row) => {
    if (row.dataset.bound === "true") return
    row.dataset.bound = "true"

    const navigate = () => {
      window.location.assign(row.dataset.href)
    }

    row.addEventListener("click", (event) => {
      if (event.target.closest("a, button, input, select, textarea")) return
      navigate()
    })

    row.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        navigate()
      }
    })
  })
}

function setupHousingMapTableSync() {
  const rows = document.querySelectorAll(".city-comparison-row")
  if (!rows.length) return

  rows.forEach((row) => {
    if (row.dataset.bound === "true") return
    row.dataset.bound = "true"
    row.addEventListener("click", () => {
      window.dispatchEvent(new CustomEvent("housing-map:city", { detail: { slug: row.dataset.citySlug } }))
    })
  })

  if (document.body.dataset.housingMapSync === "true") return
  document.body.dataset.housingMapSync = "true"

  window.addEventListener("housing-map:city", (e) => {
    const slug = e.detail?.slug
    if (!slug) return
    document.querySelectorAll(".city-comparison-row").forEach((r) => {
      r.classList.toggle("city-comparison-row--active", r.dataset.citySlug === slug)
    })
    document
      .querySelector(`.city-comparison-row[data-city-slug="${slug}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  })
}

function setupGlossary() {
  const main = document.getElementById("main")
  if (!main) return

  let terms = []
  try {
    terms = JSON.parse(main.dataset.glossaryTermsValue || "[]")
  } catch {
    return
  }

  linkGlossaryTerms(main, terms)
}

function boot() {
  initPublicSiteI18n(document.documentElement.lang)
  mountCalculators()
  initListingsBrowsePage()
  wrapGuideTables()
  initDataTableWraps()
  setupGlossary()
  setupNewsletter()
  setupCityListRows()
  setupHousingMapTableSync()
}

if (typeof window !== "undefined") {
  window.wrapGuideTables = wrapGuideTables
  window.initDataTableWraps = initDataTableWraps
  window.publicSiteLoadingHtml = loadingHtml
}

document.addEventListener("turbo:before-cache", unmountAll)
document.addEventListener("turbo:load", boot)
document.addEventListener("turbo:render", boot)

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot)
} else {
  boot()
}
